import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Circle, Rocket } from "lucide-react";

export const REPLAY_AGENTS = ["Orchestrator", "Product", "UX", "Architect", "Database", "Frontend", "Backend", "Integration", "QA", "Security", "Deploy"];
const LOGS = [
  "orchestrator  plan accepted · 11 stages queued",
  "product       brief: 4 roles · 9 features · 6 rules",
  "ux            7 screens · hash routing · mobile-first",
  "architect     system map: 14 nodes / 19 edges",
  "database      collections: bookings, barbers, services",
  "frontend      writing 9 files in parallel…",
  "backend       rule no_overlap(bookings by barber_id)",
  "integration   sms reminders → queued for Connect",
  "qa            node --check ✓  wiring ✓  smoke CRUD ✓",
  "qa            double-booking probe → 422 ✓",
  "security      secrets 0 · xss sinks escaped",
  "deploy        preview published · v1",
];

export const BuildReplay = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => (x + 1) % 17), 900); return () => clearInterval(t); }, []);
  const active = Math.min(tick, REPLAY_AGENTS.length);
  const done = tick >= REPLAY_AGENTS.length + 1;
  const checks = Math.min(48, Math.round((Math.max(0, tick - 7) / 5) * 48));
  return (
    <div data-testid="build-replay" className="glass-strong noise relative w-full overflow-hidden rounded-2xl shadow-2xl shadow-black/60">
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5c7a]" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-[#6fd6b0]" />
        <span className="label-mono ml-2 text-white/50">forge · build console</span>
        <span className="label-mono ml-auto rounded-full border border-white/15 px-2 py-0.5 text-[10px] text-white/60">Replay</span>
      </div>
      <div className="grid grid-cols-[150px_1fr] gap-0">
        <ul className="space-y-1 border-r border-white/10 p-3">
          {REPLAY_AGENTS.map((a, i) => {
            const st = i < active ? "done" : i === active && !done ? "run" : "idle";
            return (
              <li key={a} className={`flex items-center gap-2 rounded-md px-2 py-1 text-xs transition-colors duration-300 ${st === "run" ? "bg-[#7a5cff]/20 text-white" : st === "done" ? "text-white/80" : "text-white/35"}`}>
                {st === "done" ? <CheckCircle2 className="h-3.5 w-3.5 text-[#6fd6b0]" /> : st === "run" ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[#22d3ee]" /> : <Circle className="h-3.5 w-3.5" />}
                {a}
              </li>
            );
          })}
        </ul>
        <div className="flex min-h-[330px] flex-col p-3">
          <div className="flex-1 space-y-1 overflow-hidden font-mono text-[11px] leading-relaxed">
            <AnimatePresence initial={false}>
              {LOGS.slice(0, Math.min(tick + 1, LOGS.length)).slice(-9).map((l) => (
                <motion.div key={l} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} className="truncate text-white/70">
                  <span className="text-[#22d3ee]">›</span> {l}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[["checks", checks, "#6fd6b0"], ["failed", 0, "#ff5c7a"], ["repairs", tick > 10 ? 1 : 0, "#7a5cff"]].map(([k, v, c]) => (
              <div key={k} className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5">
                <div className="font-display text-lg font-bold" style={{ color: c }}>{v}</div>
                <div className="label-mono text-[9px] text-white/40">{k}</div>
              </div>
            ))}
          </div>
          <AnimatePresence>
            {done && (
              <motion.div initial={{ opacity: 0, y: 10, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} className="mt-3 rounded-xl border border-[#6fd6b0]/40 bg-[#6fd6b0]/10 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#6fd6b0]"><Rocket className="h-4 w-4" /> READY · preview live</div>
                <div className="mt-2 rounded-lg bg-white p-2">
                  <div className="h-2 w-16 rounded bg-slate-800" />
                  <div className="mt-2 grid grid-cols-3 gap-1.5">{[0, 1, 2].map((i) => <div key={i} className="h-8 rounded bg-gradient-to-br from-violet-200 to-rose-200" />)}</div>
                  <div className="mt-2 h-5 w-20 rounded-full bg-violet-500" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
